import { ChevronLeft, ChevronRight, LoaderCircle, PanelRightOpen } from 'lucide-react';
import { formatDate } from 'modules/arena/reports/model/dates';
import { formatCount, formatMoney, formatPercent, formatRating, UNAVAILABLE } from 'modules/arena/reports/model/format';
import { CellValue, ColumnKind, RatioValue, StatusValue, TableColumn, TablePage, TableQuery, TableRow } from 'modules/arena/reports/model/types';
import { Badge } from 'shared/components/ui/Badge';
import { cn } from 'shared/utils/cn';

const ONE_DECIMAL = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

function isRatio(value: CellValue): value is RatioValue {
  return typeof value === 'object' && value !== null && 'pct' in value;
}

function isStatus(value: CellValue): value is StatusValue {
  return typeof value === 'object' && value !== null && 'tone' in value;
}

export function Cell({ value, kind }: { value: CellValue; kind: ColumnKind }) {
  if (value === null || value === undefined) {
    return <span className="text-ink-secondary">{kind === 'rating' ? formatRating(null) : UNAVAILABLE}</span>;
  }
  if (isStatus(value)) return <Badge tone={value.tone}>{value.label}</Badge>;
  if (isRatio(value)) {
    return (
      <span className="whitespace-nowrap">
        {ONE_DECIMAL.format(value.value)} de {ONE_DECIMAL.format(value.capacity)}
        {value.unit ? ` ${value.unit}` : ''} <span className="text-ink-secondary">— {formatPercent(value.pct)}</span>
      </span>
    );
  }
  switch (kind) {
    case 'money': {
      const cents = Number(value);
      return <span className={cn('whitespace-nowrap tabular-nums', cents < 0 && 'text-danger')}>{formatMoney(cents)}</span>;
    }
    case 'count':
      return <span className="tabular-nums">{formatCount(Number(value))}</span>;
    case 'percent':
      return <span className="tabular-nums">{formatPercent(Number(value))}</span>;
    case 'rating':
      return <span className="whitespace-nowrap">{formatRating(Number(value))}</span>;
    case 'date':
      return <span className="whitespace-nowrap">{formatDate(String(value))}</span>;
    default:
      return <>{String(value)}</>;
  }
}

const NUMERIC: ColumnKind[] = ['money', 'count', 'percent'];

interface DataTableProps {
  title: string;
  table: TablePage;
  loading?: boolean;
  onChange?: (query: TableQuery) => void;
  onRowSelect?: (row: TableRow) => void;
  /** Explicação das colunas (ex.: o que entra em "Taxas da arena"). */
  footnote?: string;
}

export function DataTable({ title, table, loading, onChange, onRowSelect, footnote }: DataTableProps) {
  const { columns, rows, totals, page, pageSize, totalRows, sortOptions, sort } = table;
  const pages = Math.max(1, Math.ceil(totalRows / pageSize));
  const from = totalRows ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(totalRows, page * pageSize);
  const primary = columns.filter((column) => column.primary);
  const secondary = columns.filter((column) => !column.primary);

  return (
    <section aria-label={title} className="relative rounded-2xl border border-border/40 bg-surface shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border/50 px-5 py-4">
        <div>
          <h3 className="font-heading font-semibold text-ink">{title}</h3>
          <p className="text-xs text-ink-secondary">
            {totalRows ? `Mostrando ${from}–${to} de ${totalRows} resultado(s)` : 'Nenhum resultado'}
          </p>
        </div>
        {sortOptions && onChange && (
          <label className="flex items-center gap-2 text-xs font-medium text-ink-secondary">
            Ordenar por
            <select
              value={sort ?? sortOptions[0].id}
              onChange={(e) => onChange({ page: 1, sort: e.target.value })}
              className="rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {sortOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-surface/60" role="status">
          <LoaderCircle className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
          <span className="sr-only">Atualizando tabela</span>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-secondary">{table.emptyMessage ?? 'Nenhum registro.'}</p>
      ) : (
        <>
          {/* Desktop: tabela com cabeçalho fixo e totais do conjunto inteiro. */}
          <div className="hidden max-h-[560px] overflow-auto md:block">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-[1] bg-background">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={cn(
                        'whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-secondary',
                        NUMERIC.includes(column.kind) && 'text-right'
                      )}
                    >
                      {column.label}
                    </th>
                  ))}
                  {onRowSelect && <th scope="col" className="w-10"><span className="sr-only">Detalhes</span></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-border/40 hover:bg-background/70">
                    {columns.map((column) => (
                      <td key={column.key} className={cn('px-4 py-2.5 text-ink', NUMERIC.includes(column.kind) && 'text-right')}>
                        <Cell value={row.cells[column.key]} kind={column.kind} />
                      </td>
                    ))}
                    {onRowSelect && (
                      <td className="px-2">
                        {row.hasDetail && (
                          <button
                            type="button"
                            onClick={() => onRowSelect(row)}
                            aria-label={`Ver detalhes de ${row.cells[columns[1]?.key ?? columns[0].key] ?? row.id}`}
                            className="rounded-md p-1.5 text-ink-secondary hover:bg-chip hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            <PanelRightOpen className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot className="sticky bottom-0 bg-background">
                  <tr className="border-t-2 border-border">
                    {columns.map((column, index) => (
                      <td key={column.key} className={cn('px-4 py-2.5 font-semibold text-ink', NUMERIC.includes(column.kind) && 'text-right')}>
                        {column.key in totals ? (
                          index === 0 ? (
                            <span>
                              {String(totals[column.key])}
                              <span className="block text-[11px] font-normal text-ink-secondary">{totalRows} linha(s), todas as páginas</span>
                            </span>
                          ) : (
                            <Cell value={totals[column.key]} kind={column.kind} />
                          )
                        ) : null}
                      </td>
                    ))}
                    {onRowSelect && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Celular: cada linha vira um cartão que mantém o título de cada coluna. */}
          <ul className="divide-y divide-border/50 md:hidden">
            {rows.map((row) => (
              <li key={row.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {primary.map((column) => (
                    <span key={column.key} className="text-sm font-medium text-ink">
                      <span className="sr-only">{column.label}: </span>
                      <Cell value={row.cells[column.key]} kind={column.kind} />
                    </span>
                  ))}
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {secondary.map((column) => (
                    <div key={column.key} className="contents">
                      <dt className="text-ink-secondary">{column.label}</dt>
                      <dd className="text-right text-ink">
                        <Cell value={row.cells[column.key]} kind={column.kind} />
                      </dd>
                    </div>
                  ))}
                </dl>
                {onRowSelect && row.hasDetail && (
                  <button type="button" onClick={() => onRowSelect(row)} className="mt-2 text-xs font-semibold text-primary-dark hover:underline">
                    Ver detalhes
                  </button>
                )}
              </li>
            ))}
            {totals && (
              <li className="bg-background px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">Totais ({totalRows} linhas, todas as páginas)</p>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {columns
                    .filter((column, index) => index > 0 && column.key in totals)
                    .map((column) => (
                      <div key={column.key} className="contents">
                        <dt className="text-ink-secondary">{column.label}</dt>
                        <dd className="text-right font-semibold text-ink">
                          <Cell value={totals[column.key]} kind={column.kind} />
                        </dd>
                      </div>
                    ))}
                </dl>
              </li>
            )}
          </ul>
        </>
      )}

      {(pages > 1 || footnote) && (
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border/50 px-5 py-3 text-xs text-ink-secondary">
          <span>{footnote}</span>
          {pages > 1 && onChange && (
            <nav aria-label="Paginação" className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => onChange({ page: page - 1, sort })}
                className="rounded-md border border-border p-1.5 text-ink hover:bg-chip disabled:opacity-40"
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span aria-live="polite">
                Página {page} de {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages || loading}
                onClick={() => onChange({ page: page + 1, sort })}
                className="rounded-md border border-border p-1.5 text-ink hover:bg-chip disabled:opacity-40"
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </nav>
          )}
        </footer>
      )}
    </section>
  );
}

export type { TableColumn };
